import {
  InterviewSessionListResponse,
  ParseResumeRequest,
  ParseResumeResponse,
  StartInterviewRequest,
  StartInterviewResponse,
  TranscribeResponse,
  AnswerRequest,
  AnswerResponse,
  ResumeInterviewResponse,
  ResumeGradeRequest,
  ResumeGradeResponse,
  IcebreakerResponse,
  FeedbackResponse,
  FeedbackApiResponse,
  GradeResumeStartResponse,
  GradeResumeResultResponse,
  GradeResumeSessionListResponse,
  InterviewRoundListResponse,
} from "../data/interface/InterviewHubInterface";
import HttpService from "../../../service/HttpService";
import CommonUtils from "../../../utils/Common_Utils";

class InterviewHubApiService {
  private api: HttpService;

  constructor(api: HttpService) {
    this.api = api;
  }

  async listSessions(): Promise<InterviewSessionListResponse> {
    const offset_time = CommonUtils.formatLocalTime();
    const response = await this.api.get<InterviewSessionListResponse>(`/interview/sessions?offset_time=${offset_time}`);
    return response.data;
  }

  async listRounds(): Promise<InterviewRoundListResponse> {
    const response = await this.api.get<InterviewRoundListResponse>("/interview/rounds");
    return response.data;
  }

  async parseResume(data: ParseResumeRequest): Promise<ParseResumeResponse> {
    const response = await this.api.post<ParseResumeResponse>("/interview/parse-resume", data);
    return response.data;
  }

  async startInterview(data: StartInterviewRequest): Promise<StartInterviewResponse> {
    const response = await this.api.post<StartInterviewResponse>("/interview/start", data);
    return response.data;
  }

  async getIcebreaker(sessionId: string): Promise<IcebreakerResponse> {
    const response = await this.api.get<any>(
      `/interview/icebreaker?session_id=${sessionId}`
    );
    const data = response.data;

    // Backend omits the "status" field when returning the actual icebreaker text.
    if (data.icebreaker || data.icebreaker_text) {
      return {
        status: "READY",
        icebreaker_text: data.icebreaker || data.icebreaker_text,
        audio_url: data.audio_url,
      };
    }

    return {
      status: (data.status || "FAILED").toUpperCase() as "PENDING" | "FAILED",
      reason: data.reason,
    };
  }

  async getTranscribeUrl(sessionId: string): Promise<string> {
    const response = await this.api.get<{ url: string }>(
      `/interview/live_transcribe?session_id=${sessionId}`
    );
    return response.data.url;
  }

  /** @deprecated Use WebSocket live transcription via getTranscribeUrl instead */
  async transcribeAnswer(sessionId: string, file: File): Promise<TranscribeResponse> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await this.api.post<TranscribeResponse>(
      `/interview/transcribe?session_id=${sessionId}`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  }

  async submitAnswer(sessionId: string, data: AnswerRequest): Promise<AnswerResponse> {
    const response = await this.api.post<AnswerResponse>(
      `/interview/answer?session_id=${sessionId}`,
      data
    );
    return response.data;
  }

  async resumeInterview(sessionId: string): Promise<ResumeInterviewResponse> {
    const response = await this.api.get<ResumeInterviewResponse>(
      `/interview/resume/${sessionId}`
    );
    return response.data;
  }

  async getFeedback(sessionId: string): Promise<FeedbackApiResponse> {
    const response = await this.api.get<FeedbackApiResponse>(
      `/interview/feedback?session_id=${sessionId}`
    );
    return response.data;
  }

  async gradeResume(data: ResumeGradeRequest): Promise<ResumeGradeResponse> {
    const response = await this.api.post<ResumeGradeResponse>("/interview/grade-resume", data);
    return response.data;
  }

  // ─── Resume Grading (standalone) ─────────────────────────────────────────

  async startResumeGrading(data: { file_url: string }): Promise<GradeResumeStartResponse> {
    const response = await this.api.post<GradeResumeStartResponse>("/grade_resume", data);
    return response.data;
  }

  async getResumeGradeResult(sessionId: string): Promise<GradeResumeResultResponse> {
    const response = await this.api.get<GradeResumeResultResponse>(
      `/grade_resume?session_id=${sessionId}`
    );
    return response.data;
  }

  async listGradeSessions(): Promise<GradeResumeSessionListResponse> {
    const offset_time = CommonUtils.formatLocalTime();
    const response = await this.api.get<GradeResumeSessionListResponse>(
      `/grade_resume/session_list?offset_time=${offset_time}`
    );
    return response.data;
  }

  async updateHeartbeat(sessionId: string, duration: number): Promise<void> {
    await this.api.post(
      `/interview/heartbeat?session_id=${sessionId}&duration=${duration}`,
      {}
    );
  }

  // ─── Resume Optimization & JD Matching ───────────────────────────────────

  async startResumeOptimization(data: { job_description: string; resume_url: string }): Promise<{ session_id: string; status: string }> {
    const response = await this.api.post<{ session_id: string; status: string }>("/interview/jd_match", data);
    return response.data;
  }

  async getResumeOptimizationResult(sessionId: string): Promise<any> {
    const response = await this.api.get<any>(`/interview/jd_match?session_id=${sessionId}`);
    return response.data;
  }

  async listOptimizationSessions(): Promise<any> {
    const offset_time = CommonUtils.formatLocalTime();
    const response = await this.api.get<any>(`/interview/jd_match/session_list?offset_time=${offset_time}`);
    return response.data;
  }

  async handleBulletAction(data: { session_id: string; bullet_id: string; action: string; user_edit?: string }): Promise<any> {
    const response = await this.api.post<any>("/interview/bullet/action", data);
    return response.data;
  }

  async handleBulletChat(data: { session_id: string; bullet_id: string; user_input: string }): Promise<any> {
    const response = await this.api.post<any>("/interview/bullet/chat", data);
    return response.data;
  }
}

export default InterviewHubApiService;

